import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Library, Plus, Trash2, X, Search, AlertTriangle, Sparkles, Download, Wand2, BookOpen, Stars, CheckCheck, Archive, Brain } from "lucide-react";
import { toast } from "sonner";

const HTML_TYPES = [
    "text", "number", "email", "tel", "url", "password",
    "date", "datetime-local", "time", "month", "week",
    "checkbox", "radio", "select", "textarea",
    "range", "color", "file",
];
const DATA_TYPES = ["integer", "float", "string", "boolean", "date", "datetime", "time", "enum", "multi_enum"];
const STATUSES = ["defined", "pending_review", "deprecated"];
const CATEGORY_SUGGESTIONS = ["vitals", "lab", "demographics", "adverse_event", "med_history", "concomitant_medication", "consent", "questionnaire", "custom"];

const STATUS_BADGE = {
    defined: "bg-emerald-400/10 border-emerald-400/30 text-emerald-400",
    pending_review: "bg-amber-400/10 border-amber-400/30 text-amber-400",
    deprecated: "bg-white/5 border-white/20 text-white/40",
};

export default function FieldLibraryPage({ embedded = false }) {
    const [items, setItems] = useState([]);
    const [stats, setStats] = useState({ pending_review: 0, defined: 0, deprecated: 0, total_all_statuses: 0 });
    const [facets, setFacets] = useState({ categories: [], cdisc_domains: [] });
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [status, setStatus] = useState("");
    const [category, setCategory] = useState("");
    const [domain, setDomain] = useState("");
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [seeding, setSeeding] = useState(false);
    const [importing, setImporting] = useState(false);
    const [curating, setCurating] = useState(false);
    const [aiCurating, setAiCurating] = useState(false);
    // Bulk-curation state
    const [selected, setSelected] = useState(new Set());
    const [bulkBusy, setBulkBusy] = useState(null);  // 'approve'|'deprecate'|'delete'|null
    // AI CDISC suggestion modal
    const [suggestOpen, setSuggestOpen] = useState(false);
    const [suggestRow, setSuggestRow] = useState(null);
    const [suggestData, setSuggestData] = useState(null);
    const [suggestBusy, setSuggestBusy] = useState(false);

    const toggleRow = (id) => setSelected((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
    });
    const clearSelection = () => setSelected(new Set());
    const allVisibleSelected = useMemo(
        () => items.length > 0 && items.every((it) => selected.has(it.id)),
        [items, selected],
    );
    const toggleAll = () => setSelected((prev) => {
        if (allVisibleSelected) {
            const next = new Set(prev);
            items.forEach((it) => next.delete(it.id));
            return next;
        }
        const next = new Set(prev);
        items.forEach((it) => next.add(it.id));
        return next;
    });

    const load = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (q) params.set("q", q);
            if (status) params.set("status", status);
            if (category) params.set("category", category);
            if (domain) params.set("cdisc_domain", domain);
            params.set("limit", "500");
            const { data } = await api.get(`/admin/field-library?${params.toString()}`);
            setItems(data.items || []);
            setStats(data.stats || {});
            setFacets(data.facets || { categories: [], cdisc_domains: [] });
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const seedPending = async () => {
        setSeeding(true);
        try {
            const { data } = await api.post("/admin/field-library/seed-pending");
            toast.success(`Inserted ${data.inserted} new field(s) · ${data.skipped} already in library`);
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        } finally {
            setSeeding(false);
        }
    };

    const importCdisc = async () => {
        if (!confirm("Import the CDISC seed library?\n\nThis will:\n• Insert ~70 hand-curated CDISC fields (DM, VS, LB, AE, CM, MH, EX domains).\n• Auto-promote any matching pending_review rows to defined and inherit CDISC metadata + ranges + units.\n\nExisting defined rows are NOT overwritten unless you choose to do so.")) return;
        setImporting(true);
        try {
            const { data } = await api.post("/admin/field-library/import-cdisc?auto_curate=true&overwrite=false");
            toast.success(`CDISC import complete · ${data.inserted} inserted · ${data.updated_curated} pending rows curated · ${data.skipped} skipped`);
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        } finally {
            setImporting(false);
        }
    };

    const autoCurate = async () => {
        if (!confirm("Run heuristic auto-curation on all pending_review fields?\n\n• Slug-similar to a defined CDISC field → marked deprecated alias.\n• Date / yes-no / signature / email patterns → upgraded to defined with sensible defaults.\n\nNon-matching fields stay pending.")) return;
        setCurating(true);
        try {
            const { data } = await api.post("/admin/field-library/auto-curate");
            toast.success(`Auto-curate · ${data.upgraded} upgraded to defined · ${data.deprecated} deprecated as aliases · ${data.unchanged_pending} still pending`);
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        } finally {
            setCurating(false);
        }
    };

    const bulkAiCurate = async () => {
        const untouched = items.filter(
            (r) => r.status === "pending_review" && !(r.tags || []).includes("ai-curated")
        ).length;
        if (untouched === 0) {
            toast.info("No untouched pending fields — every pending row has already been reviewed by the AI.");
            return;
        }
        const estCost = (untouched * 0.0028).toFixed(2);
        if (!confirm(
            `Run AI CDISC curation on ${untouched} untouched pending field${untouched === 1 ? "" : "s"}?\n\n` +
            "Confidence policy:\n" +
            "  • HIGH   → apply suggestion + flip to defined\n" +
            "  • MEDIUM → apply suggestion, stay pending for human review\n" +
            "  • LOW    → tag and leave alone\n\n" +
            `Estimated cost: ~$${estCost} (Claude Sonnet, $0.003/field).\n` +
            "Runs in batches of 15 to stay under the ingress timeout."
        )) return;
        setAiCurating(true);
        const agg = { processed: 0, applied_high: 0, applied_medium: 0, skipped_low: 0, errored: 0, total_cost_usd: 0 };
        try {
            // Cap at 30 batches × 15 = 450 fields (safety)
            for (let i = 0; i < 30; i += 1) {
                const { data } = await api.post(
                    "/admin/field-library/bulk-suggest-cdisc?limit=15&parallelism=3",
                    {},
                    { timeout: 90000 },
                );
                agg.processed += data.processed || 0;
                agg.applied_high += data.applied_high || 0;
                agg.applied_medium += data.applied_medium || 0;
                agg.skipped_low += data.skipped_low || 0;
                agg.errored += data.errored || 0;
                agg.total_cost_usd += data.total_cost_usd || 0;
                if ((data.processed || 0) === 0) break;
            }
            toast.success(
                `AI curate complete · ${agg.applied_high} defined (high) · ` +
                `${agg.applied_medium} applied (medium, awaiting review) · ` +
                `${agg.skipped_low} low-confidence skipped · ` +
                `$${agg.total_cost_usd.toFixed(4)}` +
                (agg.errored > 0 ? ` · ⚠ ${agg.errored} errors` : "")
            );
            load();
        } catch (e) {
            toast.error("Bulk AI curation failed: " + formatApiErrorDetail(e?.response?.data?.detail || e?.message));
        } finally {
            setAiCurating(false);
        }
    };

    const exportJson = async () => {
        try {
            const { data } = await api.get("/admin/field-library/export.json");
            const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `field_library_${new Date().toISOString().slice(0, 10)}.json`;
            a.click();
            URL.revokeObjectURL(url);
            toast.success(`Exported ${data.count} field(s)`);
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        }
    };

    const remove = async (it) => {
        const reason = prompt(`Reason for deleting "${it.display_name}"?`);
        if (!reason || !reason.trim()) return;
        try {
            await api.delete(`/admin/field-library/${it.id}?reason=${encodeURIComponent(reason)}`);
            toast.success("Deleted");
            load();
        } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    };

    // ============ Bulk actions ============
    const bulkAction = async (action) => {
        const ids = Array.from(selected);
        if (ids.length === 0) { toast.error("No fields selected"); return; }
        const verb = { approve: "approve to defined", deprecate: "deprecate", delete: "delete" }[action];
        let body = { ids, action };
        if (action === "delete") {
            const reason = prompt(`Reason for deleting ${ids.length} field(s)?`);
            if (!reason || !reason.trim()) return;
            body.reason = reason.trim();
        } else if (!confirm(`${verb} ${ids.length} selected field(s)?`)) {
            return;
        }
        setBulkBusy(action);
        try {
            const { data } = await api.post("/admin/field-library/bulk-update", body);
            toast.success(
                `${verb} · matched ${data.matched} · modified ${data.modified}`,
            );
            clearSelection();
            await load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        } finally {
            setBulkBusy(null);
        }
    };

    // ============ AI CDISC suggestion ============
    const openSuggest = async (it) => {
        setSuggestRow(it); setSuggestOpen(true); setSuggestData(null); setSuggestBusy(true);
        try {
            const { data } = await api.post(`/admin/field-library/${it.id}/suggest-cdisc`);
            setSuggestData(data);
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
            setSuggestOpen(false); setSuggestRow(null);
        } finally {
            setSuggestBusy(false);
        }
    };
    const applySuggestion = async () => {
        if (!suggestData?.suggestion || !suggestRow) return;
        const s = suggestData.suggestion;
        const patch = {};
        // Only apply non-null suggested values + always promote to 'defined'.
        for (const k of ["cdisc_domain", "cdisc_identifier", "loinc_code",
                         "snomed_code", "ucum_unit", "data_type",
                         "html_input_type", "category"]) {
            if (s[k] !== null && s[k] !== undefined && s[k] !== "") patch[k] = s[k];
        }
        patch.status = "defined";
        try {
            await api.patch(`/admin/field-library/${suggestRow.id}`, patch);
            toast.success(`Promoted "${suggestRow.display_name}" to defined`);
            setSuggestOpen(false); setSuggestRow(null); setSuggestData(null);
            await load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        }
    };

    return (
        <div className={embedded ? "px-8 pb-8" : "px-8 py-8"}>
            <div className="flex items-start justify-between mb-8">
                <div>
                    {!embedded && <div className="label-xs text-[#7C3AED]">Clinical Data Standards</div>}
                    <h1 className={embedded ? "font-display text-2xl tracking-tight flex items-center gap-3" : "font-display text-4xl tracking-tight mt-2 flex items-center gap-3"}>
                        Field Library
                        {stats.pending_review > 0 && (
                            <span data-testid="pending-badge" className="px-2 py-1 text-[10px] uppercase tracking-wider bg-amber-400/10 border border-amber-400/30 text-amber-400 rounded-sm flex items-center gap-1">
                                <AlertTriangle className="w-3 h-3" strokeWidth={1.8} />
                                {stats.pending_review} pending review
                            </span>
                        )}
                    </h1>
                    {!embedded && (
                        <p className="text-sm text-white/50 mt-2 max-w-2xl">
                            Canonical clinical data point definitions — HTML type, units (UCUM),
                            hard min/max + soft warning thresholds, enum values, and CDISC /
                            LOINC / SNOMED metadata. Future form generations inherit defaults
                            from <span className="text-emerald-400">defined</span> rows.
                            Auto-seeded fields land in <span className="text-amber-400">pending_review</span>
                            until you curate them.
                        </p>
                    )}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <button onClick={importCdisc} disabled={importing} data-testid="lib-import-cdisc"
                        className="px-3 py-2 border border-[#7C3AED]/40 bg-[#7C3AED]/5 rounded-md text-xs font-medium hover:bg-[#7C3AED]/15 disabled:opacity-40 flex items-center gap-1.5 text-[#7C3AED]">
                        <BookOpen className={`w-3.5 h-3.5 ${importing ? "animate-pulse" : ""}`} strokeWidth={1.8} />
                        {importing ? "Importing…" : "Import CDISC seed"}
                    </button>
                    <button onClick={autoCurate} disabled={curating} data-testid="lib-auto-curate"
                        className="px-3 py-2 border border-amber-400/30 bg-amber-400/5 rounded-md text-xs font-medium hover:bg-amber-400/15 disabled:opacity-40 flex items-center gap-1.5 text-amber-400">
                        <Wand2 className={`w-3.5 h-3.5 ${curating ? "animate-pulse" : ""}`} strokeWidth={1.8} />
                        {curating ? "Curating…" : "Auto-curate pending"}
                    </button>
                    <button onClick={bulkAiCurate} disabled={aiCurating} data-testid="lib-bulk-ai-curate"
                        className="px-3 py-2 border border-[#7C3AED]/40 bg-[#7C3AED]/10 rounded-md text-xs font-medium hover:bg-[#7C3AED]/20 disabled:opacity-40 flex items-center gap-1.5 text-[#a78bfa]">
                        <Brain className={`w-3.5 h-3.5 ${aiCurating ? "animate-pulse" : ""}`} strokeWidth={1.8} />
                        {aiCurating ? "AI curating…" : "Bulk AI curate"}
                    </button>
                    <button onClick={seedPending} disabled={seeding} data-testid="lib-seed-pending"
                        className="px-3 py-2 border border-white/15 rounded-md text-xs font-medium hover:bg-white/5 disabled:opacity-40 flex items-center gap-1.5">
                        <Sparkles className={`w-3.5 h-3.5 ${seeding ? "animate-pulse text-[#7C3AED]" : ""}`} strokeWidth={1.8} />
                        Seed pending from memory
                    </button>
                    <button onClick={exportJson} data-testid="lib-export"
                        className="px-3 py-2 border border-white/15 rounded-md text-xs font-medium hover:bg-white/5 flex items-center gap-1.5">
                        <Download className="w-3.5 h-3.5" strokeWidth={1.8} />
                        Export
                    </button>
                    <button onClick={() => { setEditing(null); setOpen(true); }} data-testid="lib-new"
                        className="bg-[#7C3AED] text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-[#7C3AED]/90 flex items-center gap-2">
                        <Plus className="w-4 h-4" strokeWidth={1.8} /> Define field
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="lib-stats">
                <Stat label="Defined" value={stats.defined} accent="emerald" />
                <Stat label="Pending review" value={stats.pending_review} accent="amber" />
                <Stat label="Deprecated" value={stats.deprecated} accent="white" />
                <Stat label="Total" value={stats.total_all_statuses} accent="purple" />
            </div>

            <form onSubmit={(e) => { e.preventDefault(); load(); }} className="bg-[#0a0a0a] border border-white/10 rounded-md p-4 mb-4 flex items-end gap-3 flex-wrap" data-testid="lib-filters">
                <div className="flex-1 min-w-[200px]">
                    <div className="label-xs mb-1.5">Search</div>
                    <div className="relative">
                        <Search className="absolute left-3 top-2.5 w-3.5 h-3.5 text-white/40" strokeWidth={1.6} />
                        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="slug, display name, CDISC ID, LOINC…" data-testid="lib-search"
                            className="w-full bg-[#050505] border border-white/10 rounded-md pl-9 pr-3 py-2 text-sm font-mono" />
                    </div>
                </div>
                <Sel label="Status" value={status} on={setStatus} options={STATUSES} testId="lib-status" />
                <Sel label="Category" value={category} on={setCategory} options={facets.categories} testId="lib-category" />
                <Sel label="CDISC domain" value={domain} on={setDomain} options={facets.cdisc_domains} testId="lib-domain" />
                <button type="submit" className="px-4 py-2 bg-[#7C3AED] text-white rounded-md text-sm font-medium hover:bg-[#7C3AED]/90">Apply</button>
            </form>

            {/* Bulk-actions toolbar — visible when selection is non-empty */}
            {selected.size > 0 && (
                <div data-testid="lib-bulk-toolbar" className="bg-[#7C3AED]/10 border border-[#7C3AED]/30 rounded-md p-3 mb-4 flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-3">
                        <span className="text-sm font-mono text-[#a17bff]">
                            {selected.size} selected
                        </span>
                        <button onClick={clearSelection} className="text-xs text-white/60 hover:text-white">
                            clear
                        </button>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        <button onClick={() => bulkAction("approve")} disabled={!!bulkBusy} data-testid="lib-bulk-approve"
                            className="px-3 py-1.5 bg-emerald-400/15 border border-emerald-400/40 text-emerald-300 rounded-md text-xs font-medium hover:bg-emerald-400/25 disabled:opacity-40 flex items-center gap-1.5">
                            <CheckCheck className={`w-3.5 h-3.5 ${bulkBusy === "approve" ? "animate-pulse" : ""}`} strokeWidth={1.8} />
                            {bulkBusy === "approve" ? "Approving…" : "Approve to defined"}
                        </button>
                        <button onClick={() => bulkAction("deprecate")} disabled={!!bulkBusy} data-testid="lib-bulk-deprecate"
                            className="px-3 py-1.5 bg-white/5 border border-white/20 text-white/70 rounded-md text-xs font-medium hover:bg-white/10 disabled:opacity-40 flex items-center gap-1.5">
                            <Archive className={`w-3.5 h-3.5 ${bulkBusy === "deprecate" ? "animate-pulse" : ""}`} strokeWidth={1.8} />
                            {bulkBusy === "deprecate" ? "Deprecating…" : "Deprecate"}
                        </button>
                        <button onClick={() => bulkAction("delete")} disabled={!!bulkBusy} data-testid="lib-bulk-delete"
                            className="px-3 py-1.5 bg-red-500/15 border border-red-500/40 text-red-400 rounded-md text-xs font-medium hover:bg-red-500/25 disabled:opacity-40 flex items-center gap-1.5">
                            <Trash2 className={`w-3.5 h-3.5 ${bulkBusy === "delete" ? "animate-pulse" : ""}`} strokeWidth={1.8} />
                            {bulkBusy === "delete" ? "Deleting…" : "Delete"}
                        </button>
                    </div>
                </div>
            )}

            <div className="bg-[#0a0a0a] border border-white/10 rounded-md overflow-hidden">
                <table className="w-full">
                    <thead>
                        <tr className="text-left">
                            <th className="label-xs px-4 py-3 w-8">
                                <input
                                    type="checkbox"
                                    data-testid="lib-select-all"
                                    checked={allVisibleSelected}
                                    onChange={toggleAll}
                                    className="cursor-pointer accent-[#7C3AED]"
                                />
                            </th>
                            <th className="label-xs px-5 py-3">Field</th>
                            <th className="label-xs px-5 py-3">Type</th>
                            <th className="label-xs px-5 py-3">Range</th>
                            <th className="label-xs px-5 py-3">Unit</th>
                            <th className="label-xs px-5 py-3">CDISC / LOINC</th>
                            <th className="label-xs px-5 py-3">Status</th>
                            <th className="label-xs px-5 py-3">Seen</th>
                            <th className="label-xs px-5 py-3 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={9} className="px-5 py-8 text-center text-xs text-white/40 font-mono">Loading…</td></tr>
                        ) : items.length === 0 ? (
                            <tr><td colSpan={9} className="px-5 py-16 text-center">
                                <Library className="w-10 h-10 text-white/20 mx-auto mb-3" strokeWidth={1.2} />
                                <div className="text-sm text-white/60">No fields match the filter.</div>
                                <div className="text-xs text-white/40 mt-1">Click <span className="text-[#7C3AED]">Seed pending from memory</span> to auto-import every field AIService has ever generated.</div>
                            </td></tr>
                        ) : items.map((it) => (
                            <tr key={it.id} className={`data-row border-t border-white/5 cursor-pointer hover:bg-white/[0.02] ${selected.has(it.id) ? "bg-[#7C3AED]/5" : ""}`} data-testid={`lib-row-${it.slug}`}
                                onClick={(e) => {
                                    if (e.target.tagName === "INPUT" || e.target.closest("button")) return;
                                    setEditing(it); setOpen(true);
                                }}>
                                <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                                    <input
                                        type="checkbox"
                                        data-testid={`lib-select-${it.slug}`}
                                        checked={selected.has(it.id)}
                                        onChange={() => toggleRow(it.id)}
                                        className="cursor-pointer accent-[#7C3AED]"
                                    />
                                </td>
                                <td className="px-5 py-3">
                                    <div className="text-sm">{it.display_name}</div>
                                    <div className="text-[11px] font-mono text-white/40">{it.slug}</div>
                                </td>
                                <td className="px-5 py-3 font-mono text-xs">
                                    <div className="text-white/80">{it.html_input_type}</div>
                                    <div className="text-[10px] text-white/40">{it.data_type}</div>
                                </td>
                                <td className="px-5 py-3 font-mono text-xs">
                                    {it.min_value !== null && it.min_value !== undefined ? (
                                        <div>
                                            <span className="text-white/60">{it.min_value}–{it.max_value ?? "∞"}</span>
                                            {(it.warning_min !== null || it.warning_max !== null) && (
                                                <div className="text-[10px] text-amber-400">
                                                    warn {it.warning_min ?? "·"}–{it.warning_max ?? "·"}
                                                </div>
                                            )}
                                        </div>
                                    ) : <span className="text-white/30">—</span>}
                                </td>
                                <td className="px-5 py-3 font-mono text-xs">
                                    <div className="text-white/80">{it.unit || <span className="text-white/30">—</span>}</div>
                                    {it.ucum_unit && <div className="text-[10px] text-white/40">{it.ucum_unit}</div>}
                                </td>
                                <td className="px-5 py-3 font-mono text-xs">
                                    {it.cdisc_identifier && <div className="text-[#7C3AED]">{it.cdisc_identifier}</div>}
                                    {it.loinc_code && <div className="text-[10px] text-blue-400">LOINC {it.loinc_code}</div>}
                                    {!it.cdisc_identifier && !it.loinc_code && <span className="text-white/30">—</span>}
                                </td>
                                <td className="px-5 py-3">
                                    <span className={`px-2 py-0.5 text-[10px] uppercase tracking-wider border rounded-sm ${STATUS_BADGE[it.status] || STATUS_BADGE.pending_review}`}>{(it.status || "pending_review").replace("_", " ")}</span>
                                </td>
                                <td className="px-5 py-3 font-mono text-xs text-[#7C3AED]">{it.seen_count || 0}</td>
                                <td className="px-5 py-3 text-right space-x-1" onClick={(e) => e.stopPropagation()}>
                                    <button
                                        onClick={() => openSuggest(it)}
                                        title="AI suggest CDISC mapping"
                                        data-testid={`lib-suggest-${it.slug}`}
                                        className="p-1.5 rounded hover:bg-[#7C3AED]/20 text-white/70 hover:text-[#a17bff]"
                                    >
                                        <Stars className="w-3.5 h-3.5" strokeWidth={1.6} />
                                    </button>
                                    <button onClick={() => remove(it)} className="p-1.5 rounded hover:bg-red-500/20 text-white/70 hover:text-red-400" data-testid={`lib-delete-${it.slug}`}>
                                        <Trash2 className="w-3.5 h-3.5" strokeWidth={1.6} />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {open && <FieldModal field={editing} onClose={() => { setOpen(false); setEditing(null); }} onSaved={() => { setOpen(false); setEditing(null); load(); }} />}

            {suggestOpen && (
                <CdiscSuggestModal
                    row={suggestRow}
                    busy={suggestBusy}
                    data={suggestData}
                    onClose={() => { setSuggestOpen(false); setSuggestRow(null); setSuggestData(null); }}
                    onApply={applySuggestion}
                />
            )}
        </div>
    );
}

function CdiscSuggestModal({ row, busy, data, onClose, onApply }) {
    const s = data?.suggestion || {};
    const conf = s.confidence || "low";
    const confBadge = {
        high: "bg-emerald-400/10 border-emerald-400/30 text-emerald-400",
        medium: "bg-amber-400/10 border-amber-400/30 text-amber-400",
        low: "bg-white/5 border-white/20 text-white/40",
    }[conf];
    return (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onMouseDown={onClose}>
            <div className="bg-[#0c0c10] border border-white/10 rounded-2xl w-full max-w-xl p-6"
                onMouseDown={(e) => e.stopPropagation()} data-testid="cdisc-suggest-modal">
                <div className="flex items-start justify-between mb-4">
                    <div>
                        <div className="label-xs text-[#7C3AED] flex items-center gap-1.5"><Stars className="w-3 h-3" /> AI suggestion</div>
                        <h2 className="font-display text-xl mt-1">{row?.display_name}</h2>
                        <div className="text-[11px] font-mono text-white/40 mt-0.5">{row?.slug}</div>
                    </div>
                    <button onClick={onClose}><X className="w-5 h-5 text-white/50" /></button>
                </div>

                {busy ? (
                    <div className="py-12 text-center text-sm text-white/50">
                        <Stars className="w-6 h-6 text-[#7C3AED] mx-auto mb-2 animate-pulse" />
                        Asking the model… (≤45s)
                    </div>
                ) : !data ? (
                    <div className="py-8 text-center text-sm text-red-400">No suggestion received.</div>
                ) : (
                    <>
                        <div className={`inline-flex px-2 py-0.5 text-[10px] uppercase tracking-wider border rounded-sm ${confBadge} mb-4`}>
                            confidence: {conf}
                        </div>

                        <div className="grid grid-cols-2 gap-3 text-sm font-mono mb-4">
                            <SuggestRow label="CDISC domain" value={s.cdisc_domain} />
                            <SuggestRow label="CDISC identifier" value={s.cdisc_identifier} />
                            <SuggestRow label="LOINC" value={s.loinc_code} />
                            <SuggestRow label="SNOMED" value={s.snomed_code} />
                            <SuggestRow label="UCUM unit" value={s.ucum_unit} />
                            <SuggestRow label="Data type" value={s.data_type} />
                            <SuggestRow label="HTML input" value={s.html_input_type} />
                            <SuggestRow label="Category" value={s.category} />
                        </div>

                        {s.rationale ? (
                            <div className="bg-white/[0.03] border border-white/10 rounded-md p-3 mb-4 text-xs text-white/70">
                                {s.rationale}
                            </div>
                        ) : null}

                        <div className="text-[10px] font-mono text-white/30 mb-4">
                            ${(data?.cost_usd || 0).toFixed(4)} · {data?.provider}/{data?.model}
                        </div>

                        <div className="flex justify-end gap-2">
                            <button onClick={onClose} className="px-3 py-2 text-sm text-white/60">Cancel</button>
                            <button onClick={onApply} data-testid="cdisc-suggest-apply"
                                className="px-4 py-2 rounded-md bg-[#7C3AED] hover:bg-[#6928d6] text-white text-sm font-medium flex items-center gap-2">
                                <CheckCheck className="w-4 h-4" /> Apply &amp; promote to defined
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

function SuggestRow({ label, value }) {
    return (
        <div className="bg-white/[0.02] border border-white/5 rounded p-2">
            <div className="text-[10px] text-white/40 uppercase tracking-wider mb-1">{label}</div>
            <div className={value ? "text-white" : "text-white/30 italic"}>{value || "—"}</div>
        </div>
    );
}

function Stat({ label, value, accent }) {
    const colorMap = { emerald: "text-emerald-400", amber: "text-amber-400", purple: "text-[#7C3AED]", white: "text-white/80" };
    return (
        <div className="bg-[#0a0a0a] border border-white/10 rounded-md p-4">
            <div className="label-xs">{label}</div>
            <div className={`mt-1.5 text-2xl font-mono ${colorMap[accent] || "text-white"}`}>{value}</div>
        </div>
    );
}

function Sel({ label, value, on, options, testId }) {
    return (
        <div>
            <div className="label-xs mb-1.5">{label}</div>
            <select value={value} onChange={(e) => on(e.target.value)} data-testid={testId}
                className="bg-[#050505] border border-white/10 rounded-md px-3 py-2 text-sm font-mono min-w-[140px]">
                <option value="">All</option>
                {options.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
        </div>
    );
}

function FieldModal({ field, onClose, onSaved }) {
    const editing = !!field;
    const blank = {
        slug: "", display_name: "", description: "", category: "",
        html_input_type: "text", data_type: "string",
        unit: "", ucum_unit: "",
        min_value: "", max_value: "", warning_min: "", warning_max: "",
        decimal_precision: "", regex_pattern: "",
        required: false, placeholder: "", help_text: "",
        cdisc_standard: "", cdisc_identifier: "", cdisc_domain: "",
        cdisc_test_code: "", cdisc_test_name: "",
        loinc_code: "", snomed_code: "",
        status: "defined",
        enum_options: [],
    };
    const initial = field ? { ...blank, ...field, enum_options: field.enum_options || [] } : blank;
    const [v, setV] = useState(initial);
    const [loading, setLoading] = useState(false);
    const set = (k) => (val) => setV((p) => ({ ...p, [k]: val }));
    const showEnum = ["select", "radio"].includes(v.html_input_type) || ["enum", "multi_enum"].includes(v.data_type);

    const submit = async (e) => {
        e.preventDefault();
        const body = { ...v };
        ["min_value", "max_value", "warning_min", "warning_max", "decimal_precision"].forEach((k) => {
            body[k] = body[k] === "" || body[k] === null ? null : Number(body[k]);
        });
        ["unit", "ucum_unit", "category", "description", "regex_pattern", "placeholder", "help_text",
         "cdisc_standard", "cdisc_identifier", "cdisc_domain", "cdisc_test_code", "cdisc_test_name",
         "loinc_code", "snomed_code"].forEach((k) => { if (body[k] === "") body[k] = null; });
        body.enum_options = (body.enum_options || []).filter((o) => o.value && o.label);
        setLoading(true);
        try {
            if (editing) {
                await api.patch(`/admin/field-library/${field.id}`, body);
                toast.success("Updated");
            } else {
                await api.post("/admin/field-library", body);
                toast.success("Created");
            }
            onSaved();
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    const addEnum = () => setV((p) => ({ ...p, enum_options: [...(p.enum_options || []), { value: "", label: "", code: "" }] }));
    const updEnum = (i, k, val) => setV((p) => ({ ...p, enum_options: (p.enum_options || []).map((o, idx) => idx === i ? { ...o, [k]: val } : o) }));
    const delEnum = (i) => setV((p) => ({ ...p, enum_options: (p.enum_options || []).filter((_, idx) => idx !== i) }));

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" onMouseDown={(e)=>{e.currentTarget._dn=e.target===e.currentTarget;}} onMouseUp={(e)=>{if(e.currentTarget._dn&&e.target===e.currentTarget)onClose();e.currentTarget._dn=false;}}>
            <div className="w-full max-w-3xl max-h-[90vh] overflow-y-auto bg-[#0a0a0a] border border-white/15 rounded-md" onClick={(e) => e.stopPropagation()} data-testid="lib-modal">
                <div className="px-5 py-4 border-b border-white/10 flex items-center sticky top-0 bg-[#0a0a0a] z-10">
                    <Library className="w-4 h-4 text-[#7C3AED] mr-2" strokeWidth={1.8} />
                    <div className="font-display text-lg tracking-tight">{editing ? "Edit field" : "Define field"}</div>
                    <span className={`ml-3 px-2 py-0.5 text-[10px] uppercase tracking-wider border rounded-sm ${STATUS_BADGE[v.status]}`}>{v.status.replace("_", " ")}</span>
                    <button onClick={onClose} className="ml-auto text-white/60 hover:text-white"><X className="w-4 h-4" /></button>
                </div>
                <form onSubmit={submit} className="p-5 space-y-5">
                    <Section title="Identity">
                        <div className="grid grid-cols-2 gap-3">
                            <Input label="Slug" required value={v.slug} on={set("slug")} disabled={editing} hint="Stable id (snake_case). Auto-derived if empty." testId="lib-form-slug" />
                            <Input label="Display name" required value={v.display_name} on={set("display_name")} testId="lib-form-display" />
                        </div>
                        <Input label="Description" value={v.description} on={set("description")} testId="lib-form-description" />
                        <div className="grid grid-cols-3 gap-3">
                            <Datalist label="Category" value={v.category} on={set("category")} options={CATEGORY_SUGGESTIONS} testId="lib-form-category" />
                            <SelectField label="Status" value={v.status} on={set("status")} options={STATUSES} testId="lib-form-status" />
                            <SelectField label="Required" value={v.required ? "yes" : "no"} on={(x) => set("required")(x === "yes")} options={["no", "yes"]} testId="lib-form-required" />
                        </div>
                    </Section>

                    <Section title="Input">
                        <div className="grid grid-cols-2 gap-3">
                            <SelectField label="HTML input type" value={v.html_input_type} on={set("html_input_type")} options={HTML_TYPES} testId="lib-form-html-type" />
                            <SelectField label="Data type" value={v.data_type} on={set("data_type")} options={DATA_TYPES} testId="lib-form-data-type" />
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                            <Input label="Unit (display)" value={v.unit} on={set("unit")} placeholder="mmHg" testId="lib-form-unit" />
                            <Input label="UCUM unit" value={v.ucum_unit} on={set("ucum_unit")} placeholder="mm[Hg]" testId="lib-form-ucum" />
                            <Input label="Decimal precision" value={v.decimal_precision} on={set("decimal_precision")} type="number" testId="lib-form-precision" />
                        </div>
                    </Section>

                    <Section title="Validation">
                        <div className="grid grid-cols-4 gap-3">
                            <Input label="Min value" type="number" value={v.min_value} on={set("min_value")} testId="lib-form-min" />
                            <Input label="Warning min" type="number" value={v.warning_min} on={set("warning_min")} testId="lib-form-warn-min" />
                            <Input label="Warning max" type="number" value={v.warning_max} on={set("warning_max")} testId="lib-form-warn-max" />
                            <Input label="Max value" type="number" value={v.max_value} on={set("max_value")} testId="lib-form-max" />
                        </div>
                        <Input label="Regex pattern" value={v.regex_pattern} on={set("regex_pattern")} testId="lib-form-regex" />
                        <div className="grid grid-cols-2 gap-3">
                            <Input label="Placeholder" value={v.placeholder} on={set("placeholder")} testId="lib-form-placeholder" />
                            <Input label="Help text" value={v.help_text} on={set("help_text")} testId="lib-form-help" />
                        </div>
                    </Section>

                    {showEnum && (
                        <Section title="Enum options" testId="lib-form-enum-section">
                            <div className="space-y-2">
                                {(v.enum_options || []).map((o, i) => (
                                    <div key={i} className="grid grid-cols-12 gap-2 items-end" data-testid={`lib-enum-row-${i}`}>
                                        <div className="col-span-3"><Input label={i === 0 ? "Value" : ""} value={o.value} on={(x) => updEnum(i, "value", x)} testId={`lib-enum-value-${i}`} /></div>
                                        <div className="col-span-5"><Input label={i === 0 ? "Label" : ""} value={o.label} on={(x) => updEnum(i, "label", x)} testId={`lib-enum-label-${i}`} /></div>
                                        <div className="col-span-3"><Input label={i === 0 ? "Code (CT)" : ""} value={o.code || ""} on={(x) => updEnum(i, "code", x)} testId={`lib-enum-code-${i}`} /></div>
                                        <div className="col-span-1">
                                            <button type="button" onClick={() => delEnum(i)} className="p-1.5 rounded hover:bg-red-500/20 text-white/60 hover:text-red-400">
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                                <button type="button" onClick={addEnum} data-testid="lib-enum-add"
                                    className="text-xs font-mono text-[#7C3AED] hover:text-[#9F66F5] flex items-center gap-1">
                                    <Plus className="w-3 h-3" /> add option
                                </button>
                            </div>
                        </Section>
                    )}

                    <Section title="Standards (CDISC / LOINC / SNOMED)">
                        <div className="grid grid-cols-2 gap-3">
                            <Datalist label="CDISC standard" value={v.cdisc_standard} on={set("cdisc_standard")} options={["SDTM", "CDASH", "ADaM", "LOINC", "SNOMED"]} testId="lib-form-std" />
                            <Input label="CDISC identifier" value={v.cdisc_identifier} on={set("cdisc_identifier")} placeholder="VS.SYSBP" testId="lib-form-cdisc-id" />
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                            <Input label="CDISC domain" value={v.cdisc_domain} on={set("cdisc_domain")} placeholder="VS" testId="lib-form-cdisc-domain" />
                            <Input label="Test code" value={v.cdisc_test_code} on={set("cdisc_test_code")} placeholder="SYSBP" testId="lib-form-cdisc-tc" />
                            <Input label="Test name" value={v.cdisc_test_name} on={set("cdisc_test_name")} testId="lib-form-cdisc-tn" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <Input label="LOINC code" value={v.loinc_code} on={set("loinc_code")} placeholder="8480-6" testId="lib-form-loinc" />
                            <Input label="SNOMED code" value={v.snomed_code} on={set("snomed_code")} testId="lib-form-snomed" />
                        </div>
                    </Section>

                    <div className="flex justify-end gap-2 pt-2">
                        <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-white/15 rounded-md hover:bg-white/5">Cancel</button>
                        <button type="submit" disabled={loading} data-testid="lib-form-submit"
                            className="px-4 py-2 bg-[#7C3AED] text-white rounded-md text-sm font-medium hover:bg-[#7C3AED]/90 disabled:opacity-40">
                            {loading ? "Saving…" : (editing ? "Update" : "Create")}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function Section({ title, children, testId }) {
    return (
        <div data-testid={testId}>
            <div className="label-xs text-[#7C3AED] mb-3 pb-2 border-b border-white/5">{title}</div>
            <div className="space-y-3">{children}</div>
        </div>
    );
}

function Input({ label, value, on, placeholder, required, hint, type = "text", disabled, testId }) {
    return (
        <div>
            {label && <div className="label-xs mb-1.5">{label}{required && <span className="text-[#7C3AED] ml-1">*</span>}</div>}
            <input type={type} value={value === null ? "" : value} disabled={disabled} required={required} placeholder={placeholder} onChange={(e) => on(e.target.value)} data-testid={testId}
                className="w-full bg-[#050505] border border-white/10 rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:border-white/30 disabled:opacity-50" />
            {hint && <div className="text-[11px] text-white/40 mt-1">{hint}</div>}
        </div>
    );
}

function SelectField({ label, value, on, options, testId }) {
    return (
        <div>
            <div className="label-xs mb-1.5">{label}</div>
            <select value={value} onChange={(e) => on(e.target.value)} data-testid={testId}
                className="w-full bg-[#050505] border border-white/10 rounded-md px-3 py-2 text-sm font-mono">
                {options.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
        </div>
    );
}

function Datalist({ label, value, on, options, testId }) {
    const id = useMemo(() => `dl-${Math.random().toString(36).slice(2, 8)}`, []);
    return (
        <div>
            <div className="label-xs mb-1.5">{label}</div>
            <input list={id} value={value || ""} onChange={(e) => on(e.target.value)} data-testid={testId}
                className="w-full bg-[#050505] border border-white/10 rounded-md px-3 py-2 text-sm font-mono" />
            <datalist id={id}>
                {options.map((o) => <option key={o} value={o} />)}
            </datalist>
        </div>
    );
}
